import { MarketingPermissions } from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getReturnsFacets } from "@redotech/redo-merchant-app-common/client/return";
import { ReviewingPartyFilter } from "@redotech/redo-return-core-common/return-search-filters";
import { createContext, memo, ReactNode, useEffect, useState } from "react";
import { settledReviewCounts } from "./orders-review-count";

export interface OrdersReviewCount {
  returnsNeedingReview?: number;
  claimsNeedingReview?: number;
  warrantiesNeedingReview?: number;
  moClaimsNeedingReview?: number;
  roClaimsNeedingReview?: number;
}

export const OrdersReviewCountContext = createContext<
  OrdersReviewCount | undefined
>(undefined);

export const OrdersReviewCountService = memo(function OrdersReviewCountService({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const client = useRequiredContext(RedoMerchantClientContext);
  const canViewMarketing = useHasPermission(
    MarketingPermissions.VIEW_MARKETING,
  );
  const [returnsNeedingReview, setReturnsNeedingReview] = useState<
    number | undefined
  >(undefined);
  const [claimsNeedingReview, setClaimsNeedingReview] = useState<
    number | undefined
  >(undefined);
  // Merchant owned claims needing review
  const [moClaimsNeedingReview, setMoClaimsNeedingReview] = useState<
    number | undefined
  >(undefined);
  // Redo owned claims needing review
  const [roClaimsNeedingReview, setRoClaimsNeedingReview] = useState<
    number | undefined
  >(undefined);
  const [warrantiesNeedingReview, setWarrantiesNeedingReview] = useState<
    number | undefined
  >(undefined);

  const [_, fetchReturnsNeedingReviewCounts] = useTriggerLoad(
    async (signal) => {
      if (!canViewMarketing) {
        return;
      }
      const requests = [
        {
          label: "returns",
          setCount: setReturnsNeedingReview,
          facets: getReturnsFacets(client, { type: "return", signal }),
        },
        {
          label: "claims",
          setCount: setClaimsNeedingReview,
          facets: getReturnsFacets(client, { type: "claim", signal }),
        },
        {
          label: "warranties",
          setCount: setWarrantiesNeedingReview,
          facets: getReturnsFacets(client, { type: "warranty", signal }),
        },
        {
          label: "merchant-owned claims",
          setCount: setMoClaimsNeedingReview,
          facets: getReturnsFacets(client, {
            type: "claim",
            signal,
            reviewingParty: ReviewingPartyFilter.MERCHANT_REVIEW,
          }),
        },
        {
          label: "Redo-owned claims",
          setCount: setRoClaimsNeedingReview,
          facets: getReturnsFacets(client, {
            type: "claim",
            signal,
            reviewingParty: ReviewingPartyFilter.NOT_MERCHANT_REVIEW,
          }),
        },
      ];
      // Settled, not all: one facet request failing must not clear the others.
      const updates = settledReviewCounts(
        await Promise.allSettled(requests.map((request) => request.facets)),
        signal.aborted,
      );
      if (!updates) {
        return;
      }
      for (const [index, update] of updates.entries()) {
        const request = requests[index];
        if (!request) {
          continue;
        }
        if (update.status === "failed") {
          // Constant format string; the label is a separate field so a
          // dynamic value cannot forge the log (SAST: unsafe-formatstring).
          console.error("Error fetching review counts", {
            error: update.error,
            label: request.label,
          });
          request.setCount(undefined);
          continue;
        }
        request.setCount(update.count);
      }
    },
  );

  useEffect(() => {
    let mounted = true;
    // Background tabs keep their badge; they refresh on return to focus.
    const fetchCounts = () => {
      if (!mounted || document.hidden) {
        return;
      }
      try {
        fetchReturnsNeedingReviewCounts();
      } catch (e: any) {
        if (e.name !== "AbortError") {
          console.error("Error fetching counts:", e);
        }
      }
    };
    fetchCounts();
    const intervalId = setInterval(fetchCounts, 60 * 1000);
    document.addEventListener("visibilitychange", fetchCounts);
    return () => {
      mounted = false;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", fetchCounts);
    };
  }, [client, fetchReturnsNeedingReviewCounts]);

  return (
    <OrdersReviewCountContext.Provider
      value={{
        returnsNeedingReview,
        claimsNeedingReview,
        warrantiesNeedingReview,
        moClaimsNeedingReview,
        roClaimsNeedingReview,
      }}
    >
      {children}
    </OrdersReviewCountContext.Provider>
  );
});
